import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import RejectionField from '../../../../components/dialog/RejectionField';
import LoadingButton from '../../../../components/inputs/LoadingButton';
import ConfirmationDialog from '../../../../components/messages/ConfirmationDialog';
import ErrorToast, { showToast } from '../../../../components/messages/ErrorAlert';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { BillingDraft } from '../../../../Interfaces/Accounting/ContractBillings.interface';
import { ApiError } from '../../../../Interfaces/system/Generic.interfaces';
import { Access } from '../../../../Interfaces/admin/Role.interfaces';
import {
  putProgressBillingApproveDraft,
  postProgressBillingDraft,
  putProgressBillingSubmitDraft,
  putProgressBillingDraft,
  putProgressBillingRejectDraft,
  putProgressBillingUploadDraft,
} from '../../../../services/accounting/ContractBillingsService';
import { fetchContractBillingFilters } from '../CBFilters';
import { EMPTY_VALUE } from '@/src/utils/cbConst';

type ProgressBillingProcessProps = {
  draft: BillingDraft;
  defaultDraftID?: string;
  isSubmitted?: boolean;
  approvalMode?: boolean;
  access: Access;
  blockAll?: boolean;
  defaultBlockSave?: boolean;
  resetDraft: () => void;
  afterApproval?: (draftID: string) => void;
};

const ProgressBillingProcess = ({
  draft,
  defaultDraftID,
  isSubmitted,
  approvalMode,
  access,
  blockAll,
  defaultBlockSave = true,
  resetDraft,
  afterApproval,
}: ProgressBillingProcessProps) => {
  const { selectedCompany } = useCompanyContext();
  const [blockSave, setBlockSave] = useState(defaultBlockSave);
  const [blockSubmit, setBlockSubmit] = useState(
    !defaultBlockSave || (isSubmitted ?? true)
  );
  const [blockApproval, setBlockApproval] = useState(
    approvalMode ? !defaultBlockSave : !defaultDraftID || !defaultBlockSave
  );
  const [hasBeenApproved, setHasBeenApproved] = useState(false);
  const [draftID, setDraftID] = useState<string>(defaultDraftID ?? '');
  const [visibility, setVisibility] = useState(false);
  const contract = useWatch<fetchContractBillingFilters>({
    name: 'contract',
  }) as string;
  const toast = useRef<Toast>(null);
  const prevDraft = useRef(draft);

  const save = useMutation({
    mutationFn: (draftID: string) => {
      const smAmounts = draft.smAmounts;
      if (smAmounts) {
        Object.keys(smAmounts).forEach((key) => {
          const smValues = smAmounts[parseInt(key)];
          if (!smValues.purchaseSM) {
            delete smValues.purchaseSM;
          }

          if (!smValues.productionSM) {
            delete smValues.productionSM;
          }

          const keys = Object.keys(smValues);
          if (!keys.length) {
            delete smAmounts[parseInt(key)];
          }
        });
      }

      if (draftID) {
        return putProgressBillingDraft(
          draftID,
          !!approvalMode,
          draft.billing,
          draft.smAmounts,
          draft.retainage ?? undefined,
          draft.billMonth,
          draft.billGroup === EMPTY_VALUE ? undefined : draft.billGroup
        );
      }

      return postProgressBillingDraft(
        selectedCompany!.id,
        contract,
        draft.billing,
        draft.smAmounts,
        draft.retainage ?? undefined,
        draft.billMonth,
        draft.billGroup === EMPTY_VALUE ? undefined : draft.billGroup
      );
    },
    onSuccess: (data) => {
      showToast(
        'success',
        toast,
        'Save Progress Billing',
        'The data was saved successfully!',
        3000
      );

      setBlockSave(true);
      setBlockSubmit(false);
      setBlockApproval(false);

      if (!draftID) {
        setDraftID(data);
      }
    },
    onError: (error: ApiError) => {
      const errorMessage = error.response?.data?.message;

      showToast(
        'error',
        toast,
        'Save Progress Billing',
        errorMessage ?? "The data couldn't be saved",
        3000
      );
    },
  });

  const submit = useMutation({
    mutationFn: (draftID: string) => {
      return putProgressBillingSubmitDraft(draftID);
    },
    onSuccess: () => {
      setBlockSubmit(true);

      showToast(
        'success',
        toast,
        'Submit Progress Billing',
        'The data was submitted successfully!',
        3000
      );
    },
    onError: () => {
      showToast(
        'error',
        toast,
        'Submit Progress Billing',
        "The data couldn't be submitted",
        3000
      );
    },
  });

  const approveRequest = useMutation({
    mutationFn: (draftID: string) => {
      return putProgressBillingApproveDraft(draftID, selectedCompany!.id);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Approve Progress Billing`,
        `The data was approved successfully!`,
        3000
      );

      setBlockApproval(true);
      setHasBeenApproved(true);
      if (afterApproval) afterApproval(draftID);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        `Approve Progress Billing`,
        errorData?.message || `The data couldn't be approved`,
        3000
      );
    },
  });

  const uploadRequest = useMutation({
    mutationFn: (draftID: string) => {
      return putProgressBillingUploadDraft(draftID, selectedCompany!.id);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Upload Progress Billing`,
        `The data was uploaded successfully!`,
        3000
      );

      setBlockApproval(true);
      setHasBeenApproved(true);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        `Upload Progress Billing`,
        errorData?.message || `The data couldn't be uploaded`,
        3000
      );
    },
  });

  const rejectRequest = useMutation({
    mutationFn: (props: { draftID: string; reason: string }) => {
      return putProgressBillingRejectDraft(props.draftID, props.reason);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Reject Progress Billing`,
        `The draft was rejected successfully!`,
        3000
      );

      setBlockApproval(true);
      setHasBeenApproved(true);
      if (afterApproval) afterApproval(draftID);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };

      showToast(
        'error',
        toast,
        `Reject Progress Billing`,
        errorData?.message || `The draft couldn't be rejected`,
        3000
      );
    },
  });

  useEffect(() => {
    if (prevDraft.current !== draft && !hasBeenApproved) {
      setBlockSave(false);
      setBlockSubmit(true);
      setBlockApproval(true);
      prevDraft.current = draft;
    }
  }, [draft, hasBeenApproved]);

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <div className="sm:mr-auto">
        <ConfirmationDialog
          tagKey="draft-clear"
          Button={
            <LoadingButton
              label="Clear Form"
              fontSize="text-xl"
              bgColor="bg-orange-500"
              isLoading={false}
              type="button"
              disabled={hasBeenApproved}
            />
          }
          onConfirm={() => {
            resetDraft();
          }}
          message={'Are you sure you want to clear the form data?'}
        />
      </div>
      <div className="flex flex-wrap gap-4">
        {((access.shouldApprove && approvalMode) || access?.editable) && (
          <LoadingButton
            label="Save"
            fontSize="text-xl"
            bgColor="buttonSecondary"
            isLoading={save.isLoading}
            onClick={() => save.mutate(draftID)}
            disabled={blockAll || blockSave || save.isLoading}
          />
        )}
        {!approvalMode && access?.editable === 'approval' && (
          <ConfirmationDialog
            Button={
              <LoadingButton
                label="Submit"
                fontSize="text-xl"
                isLoading={submit.isLoading}
                disabled={blockAll || blockSubmit || submit.isLoading}
                type="button"
              />
            }
            onConfirm={() => submit.mutate(draftID)}
            message="Are you sure you want to submit this draft?"
          />
        )}
        {approvalMode && access.shouldApprove && (
          <ConfirmationDialog
            tagKey="draft-rejection"
            visibility={visibility}
            Button={
              <LoadingButton
                label={'Reject'}
                fontSize="text-xl"
                bgColor="bluwaiRed"
                isLoading={rejectRequest.isLoading}
                disabled={blockAll || blockApproval || rejectRequest.isLoading}
                type="button"
                onClick={() => setVisibility(true)}
              />
            }
            contentClassName="pb-0"
            onConfirm={() => {}}
            acceptClassName="hidden"
            rejectClassName="hidden"
            onHide={() => setVisibility(false)}
            message={
              <RejectionField
                onReject={() => {
                  setVisibility(false);
                }}
                onConfirm={(reason) => {
                  rejectRequest.mutate({ draftID, reason });
                  setVisibility(false);
                }}
              />
            }
          />
        )}
        {approvalMode && access?.shouldApprove && (
          <ConfirmationDialog
            tagKey="draft-approval"
            Button={
              <LoadingButton
                label={'Approve'}
                fontSize="text-xl"
                bgColor="bg-green-500"
                isLoading={approveRequest.isLoading}
                disabled={blockAll || blockApproval || approveRequest.isLoading}
                type="button"
              />
            }
            onConfirm={() => approveRequest.mutate(draftID)}
            message={`Are you sure you want to approve this draft?`}
          />
        )}
        {!approvalMode && access?.editable === 'no-approval' && (
          <ConfirmationDialog
            tagKey="draft-direct-upload"
            Button={
              <LoadingButton
                label={'Upload'}
                fontSize="text-xl"
                bgColor="bg-green-500"
                isLoading={uploadRequest.isLoading}
                disabled={blockAll || blockApproval || uploadRequest.isLoading}
                type="button"
              />
            }
            onConfirm={() => uploadRequest.mutate(draftID)}
            message={`Are you sure you want to upload this draft?`}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default ProgressBillingProcess;
